import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auth uses a Bearer token (localStorage) rather than credentialed cookies so the
// app works across the platform ingress, which returns a wildcard CORS origin.
export const api = axios.create({ baseURL: API });

// A Client Preview session lives in sessionStorage so it stays inside its own tab and
// never replaces the staff member's real login.
export const previewToken = () => sessionStorage.getItem("asthetik_preview_token");

api.interceptors.request.use((config) => {
  const preview = previewToken();
  if (preview) {
    config.headers["X-Preview-Token"] = preview;
    return config;
  }
  const token = localStorage.getItem("asthetik_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const fileUrl = (fileId) => {
  const preview = previewToken();
  if (preview) return `${API}/files/${fileId}/download?preview=${preview}`;
  const token = localStorage.getItem("asthetik_token");
  return `${API}/files/${fileId}/download${token ? `?auth=${token}` : ""}`;
};

// A project cover is a stored file when available, with legacy pasted URLs still honoured.
export const coverSrc = (p) =>
  p?.cover_file_id ? fileUrl(p.cover_file_id) : p?.cover_image || "";

// Focal point keeps the important part of a cover in frame when it is cropped.
export const coverPosition = (p) => p?.cover_focus || "50% 50%";

// A product's picture is a stored file when available, with pasted URLs still honoured.
export const productImage = (p) =>
  p?.primary_image_file_id ? fileUrl(p.primary_image_file_id) : p?.primary_image || "";

export const downloadFile = async (path, fallbackName) => {
  const { data, headers } = await api.get(path, { responseType: "blob" });
  const match = /filename="?([^"]+)"?/.exec(headers["content-disposition"] || "");
  const url = window.URL.createObjectURL(data);
  const link = document.createElement("a");
  link.href = url;
  link.download = decodeURIComponent(match?.[1] || fallbackName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export function apiError(e) {
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((d) => (d && typeof d.msg === "string" ? d.msg : JSON.stringify(d))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return e?.message || "Something went wrong.";
}

export const money = (v) =>
  (Number(v) || 0).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

export const money2 = (v) =>
  (Number(v) || 0).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 });

export const dateShort = (d) =>
  d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

export const timeAgo = (d) => {
  if (!d) return "";
  const secs = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (secs < 60) return "just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return dateShort(d);
};

export default api;
